<template>
    <li
        class="dle"
        :class="{ 'dle--active': active, 'dle--highlighted': highlighted }"
    >
        <img :src="image?.img" class="dle__img" />
        <div class="dle__information">
            <span>{{ caratShapeText }}</span>
            <span>{{ colorText }}</span>
            <span>{{ clarityText }}</span>
        </div>
        <div class="dle__price">
            <span v-if="highlighted" class="dle__price--highlighted">
                {{ highlightedText }}
            </span>
            <span>{{ priceHumanReadable }}</span>
        </div>
        <div v-if="videoLink" class="dle__camera_icon">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
            </svg>
        </div>
        <button
            type="button"
            class="dle__info-btn"
            @click="handleShowInfoButton"
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
            </svg>
        </button>
    </li>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";
import useDiamondProperties from "@/composables/useDiamondProperties";
import useDiamondShapeImageMap from "@/composables/useDiamondShapeImageMap";
import { Diamond } from "@/api/Diamond";
import { useI18n } from "vue-i18n";
import store from "@/store/store";

export default defineComponent({
    name: "DiamondListEntry",
    props: {
        diamond: {
            type: Object as PropType<Diamond>,
            required: true
        },
        active: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    setup(props) {
        const diamondShapeImageMap = useDiamondShapeImageMap();

        const { t } = useI18n();

        const image = computed(() => diamondShapeImageMap[props.diamond.shape]);
        const {
            carat,
            color,
            shape,
            clarity,
            priceHumanReadable,
            highlighted,
            videoLink
        } = useDiamondProperties(computed(() => props.diamond));

        const caratShapeText = computed(
            () =>
                `${carat.value} ${t("components.ct")} ${t(
                    `diamondProperties.shape.${shape.value}`
                )}`
        );
        const colorText = computed(
            () =>
                `${t("components.color")}: ${t(
                    `diamondProperties.color.${color.value}`
                )} (${color.value})`
        );
        const clarityText = computed(
            () =>
                `${t("components.clarity")}: ${t(
                    `diamondProperties.clarity.${clarity.value}`
                )}`
        );
        const highlightedText = computed(() => t("components.highlighted"));

        function handleShowInfoButton() {
            store.actions.setInfoSelectedDiamond(props.diamond);
            store.actions.setShowInfoModal(true);
        }

        return {
            image,
            caratShapeText,
            colorText,
            clarityText,
            priceHumanReadable,
            highlighted,
            videoLink,
            highlightedText,
            handleShowInfoButton
        };
    }
});
</script>
